<template>
  <div class="wrapper" :class="{ fullHeight: open }">
    <a href="#" @click.prevent class="screenReaderOnly">Top of content</a>
    <v-touch v-if="isMobile" class="transparentClickCapture" @vertical-swipe.prevent="toggleAnnotations" @tap.prevent="toggleAnnotations">
      <div :class=" { swipeBar: !open, closeBar: open }"></div>
    </v-touch>
    <div class="annotation" v-if="districtData">
      <h3 v-if="districtData.redistricting_text">{{stateAnnotations.name}}'s redistricting process</h3>
      <p v-if="districtData.redistricting_text" class="text">{{ districtData.redistricting_text }}</p>
      <h3 v-if="districtData.facts">Did you know?</h3>
      <p v-if="districtData.facts" class="text">{{ districtData.facts }}</p>
      <transition name="slide-fade">
        <div v-if="isMobile && open" class="attribution">
          <p><i>Map data provided by:</i></p>
          <p>
            <span v-html="attribution"></span>
          </p>
        </div>
      </transition>
    </div>
    <div v-else class="annotation">
      <h1>District Time Machine</h1>
      <p>
        Congressional districts are redrawn based on new census data every 10 years, but these maps can be challenged and revised, following each state’s unique redistricting process and rules.
      </p>
      <p>Explore your district’s unique history.</p>
    </div>
    <div v-if="!open">
      <div class="searchContainer">
        <SearchBox :isMobile="isMobile" :updateLocation="updateLocation" :hidePlaceholder="isMobile" :showLoading="showLoading" placeholderText="Enter another location" v-on:search-click="stop" />
      </div>
    </div>
  </div>
</template>

<script>
import SearchBox from './SearchBox';
import PlayButton from './PlayButton';
import AnnotationSummary from './AnnotationSummary';

export default {
  name: 'AnnotationContent',
  components: {
    SearchBox,
    PlayButton,
    AnnotationSummary
  },
  props: ['isMobile', 'updateLocation', 'showLoading',
          'play', 'stop', 'isPlaying', 'stateAnnotations',
          'latestDistrictCode', 'numDistricts', 'totalTimelineSpan',
          'attribution'],
  data() {
    return {
      open: false,
      wasPlaying: false
    };
  },
  computed: {
    districtData () {
      if (!this.stateAnnotations) return null;

      return Object.assign(this.stateAnnotations, {
        redistricting_text: this.redistrictingText
      });
    },
    redistrictingText () {
      let redistricting_text = null;

      switch (this.stateAnnotations.redistricting_type) {
        case 'legislature':
          redistricting_text = `As in most states, the ${this.stateAnnotations.name} state legislature `;
          redistricting_text += `has the power to draw new congressional district boundaries.`;
          break;

        case 'political_commission':
          redistricting_text = `In ${this.stateAnnotations.name}, a political commission `;
          redistricting_text += `is responsible for drawing new congressional district boundaries.`;
          break;

        case 'independent':
          redistricting_text = `${this.stateAnnotations.name} is one of only 4 states where an independent commission `;
          redistricting_text += `is responsible for drawing new congressional district boundaries, `;
          redistricting_text += `insulating the process from political influence.`;
          break;

        case 'at_large':
          redistricting_text = `Unless ${this.stateAnnotations.name}'s population grows to represent a great enough share of the country, there will be no need to redraw its single congressional district!`;
          break;
      }

      return redistricting_text;
    },
  },
  mounted() {
    // Put keyboard focus at top of content
    this.$el.querySelectorAll('a')[0].focus();
  },
  methods: {
    toggleAnnotations () {
      this.open = !this.open;

      // If currently playing and content is opening
      // pause animations temporarily
      if (this.isPlaying && this.open) {
        this.wasPlaying = true;
        this.stop();
      }
      // If was playing and content is closing
      // start playing again after short pause
      else if (this.wasPlaying && !this.open) {
        window.setTimeout(() => {
          this.play();
        }, 500);
      }
    }
  }
}

</script>

<style lang="scss" scoped>
@import '~styles/variables.scss';
.wrapper {
  padding: 15px;
  background: #fff;
}
.annotation {
  margin: 10px 15px 15px;
  h3{
    margin: 0;
    font-size: 16px;
    @include font-family-standard-bold;
  }
  p {
    margin-top: 0;
    font-size: 16px;
    line-height: 1.5;
    color: #000;
  }
}
.annotation a {
  font-size: 12px;
  font-weight: bold;
}
.buttonContainer {
  margin: 15px 0;
}
.searchText {
  font-size: 1.125rem;
  font-weight: bold;
  font-variant: all-small-caps;
  margin: 0 0 0.5em;
}

/*** Vue transition: slide-fade ***/
.slide-fade-enter-active {
  transition: all $transitionDuration $transitionEasing;
}
.slide-fade-leave-active {
  transition: all $transitionDuration $transitionEasing;
}
.slide-fade-enter, .slide-fade-leave-to  {
  transform: translateY(100px);
  opacity: 0;
}

.mq-mobile {
  .wrapper {
    position: fixed;
    left: 0;
    bottom: 0;
    box-sizing: border-box;
    width: 100vw;
    height: $annotationHeight;
    padding: 0.5em;
    transition: height $transitionDuration $transitionEasing;

    // Transparent div used for capturing open/close clicks
    $closeBarHeight: 50px;
    .transparentClickCapture {
      position: absolute;
      height: $closeBarHeight;
      width: 100vw;
      top: 0;
      left: 0;
      z-index: 3001;
    }
    .swipeBar {
      border-top: 5px solid $lightTextColor;
      width: 45px;
      margin: 0.8em auto 1.25em;
    }
    .closeBar {
      position: absolute;
      top: 0;
      left: 0;
      height: $closeBarHeight;
      width: 100%;
      background: $darkBackgroundColor;
      &::after {
        content: '+';
        position: absolute;
        top: -5px;
        right: 15px;
        transform: rotate(45deg);
        font-size: 45px;
        color: #fff;
      }
    }
    &.fullHeight {
      height: 60%;
      padding-top: $closeBarHeight;
    }
  }
  .annotation {
    margin: 20px;
  }
  .searchContainer {
    position: fixed;
    bottom: calc(45px + 12vh + 1.5em);
    right: 15px;
    height: 45px;
  }
  .attribution {
    position: absolute;
    bottom: 15px;
    font-weight: normal;

    p {
      margin: 0;
      font-size: 0.6875rem;
      color: $grey-40;
    }
  }
}
</style>

<style lang="scss">
// v-html will not pick up scoped scss
.annotation .attribution a {
  color: #999;
  font-weight: normal;
}
</style>



// WEBPACK FOOTER //
// src/components/AnnotationContent.vue